"use client";

import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import Icon, { ICON_SIZES } from "@/components/library/Icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { createPrintSrcDocIframe } from "@/utils/common/iframeSandbox";
import { getResponseClipboardContent } from "@/utils/chat/responseClipboard";

type CopyProps = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  copyText?: string;
  className?: string;
};

export default function Copy({ containerRef, copyText, className }: CopyProps) {
  const { t: ct } = useTranslation("common");
  const { t } = useTranslation("ask");
  const copyTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  React.useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    };
  }, []);

  const getPrintContainer = (): HTMLDivElement | null => {
    const root = containerRef.current;
    if (!root) return null;
    const wrapper = document.createElement("div");
    wrapper.innerHTML = root.innerHTML;
    wrapper
      .querySelectorAll(
        [
          ".copy-table-overlay",
          "[data-copy-control]",
          '[role="tooltip"]',
          "[data-tooltip]",
        ].join(",")
      )
      .forEach((el) => el.remove());

    return wrapper as HTMLDivElement;
  };

  const markCopied = () => {
    setCopied(true);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const copyAsMarkdown = async () => {
    const md = copyText?.trim();
    if (!md) return;
    await navigator.clipboard.writeText(md);
    markCopied();
  };

  const copyAsText = async () => {
    if (!containerRef.current) return;
    const {text: plain} = getResponseClipboardContent(containerRef.current);
    if (!plain) return;
    await navigator.clipboard.writeText(plain);
    markCopied();
  };

  const copyWithFormatting = async () => {
    if (!containerRef.current) return;
    const {html, text: plain} = getResponseClipboardContent(containerRef.current);
    if (!html) return;

    try {
      if (
        typeof ClipboardItem !== "undefined" &&
        typeof navigator.clipboard.write === "function"
      ) {
        const data = {
          "text/html": new Blob([html], { type: "text/html" }),
          ...(plain
            ? { "text/plain": new Blob([plain], { type: "text/plain" }) }
            : {}),
        } as Record<string, Blob>;
        await navigator.clipboard.write([new ClipboardItem(data)]);
      } else {
        await navigator.clipboard.writeText(plain || "");
      }
      markCopied();
    } catch (_e) {
      await navigator.clipboard.writeText(plain || "");
      markCopied();
    }
  };

  const exportAsPdf = () => {
    if (typeof window === "undefined") return;
    const wrapper = getPrintContainer();
    if (!wrapper) return;
    const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Gail Export</title>
    <style>
      @page { size: A4; margin: 1in; }
      html, body { background: #ffffff; color: #000000; }
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
        font-size: 12pt; line-height: 1.5; margin: 0;
      }
      h1, h2, h3, h4, h5, h6 { page-break-after: avoid; margin: 1.2em 0 .6em; }
      p { margin: .6em 0; }
      ul, ol { padding-left: 1.5em; margin: .6em 0; }
      pre {
        background: #f5f5f5; padding: .75em; border-radius: 6px;
        white-space: pre-wrap; word-break: break-word; page-break-inside: avoid;
      }
      code {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      }
      blockquote {
        border-left: 3px solid #ddd; margin: .6em 0; padding: .1em 1em; page-break-inside: avoid;
      }
      table { width: 100%; border-collapse: collapse; margin: .6em 0; page-break-inside: auto; }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; page-break-after: auto; }
      th, td { border: 1px solid #ddd; padding: .4em .6em; vertical-align: top; }
      img { max-width: 100%; page-break-inside: avoid; }
    </style>
  </head>
  <body>
    <article class="content">${wrapper.innerHTML}</article>
  </body>
</html>`;
    const iframe = createPrintSrcDocIframe(html);
    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => iframe.remove(), 1000);
      }
    };
  };

  return (
    <>
      <Tooltip open={dropdownOpen ? false : undefined}>
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger
              className={clsx(
                "inline-flex items-center justify-center size-8 rounded-lg",
                "opacity-70 hover:opacity-100",
                "hover:bg-black/10 dark:hover:bg-white/10",
                "transition-all duration-150",
                className
              )}
              aria-label="Copy options"
            >
              <Icon icon="copy" size={ICON_SIZES.SM} />
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t("copyResponse")}</TooltipContent>
          <DropdownMenuContent className="bg-panel" align="start">
            {copyText && (
              <>
                <CopyMenuItem
                  onClick={copyAsText}
                  label={ct("copyOptions.copyAsText.label")}
                  description={ct("copyOptions.copyAsText.description")}
                  icon={<Icon icon="text" size={ICON_SIZES.SM} />}
                />
                <CopyMenuItem
                  onClick={copyWithFormatting}
                  label={ct("copyOptions.copyWithFormatting.label")}
                  description={ct("copyOptions.copyWithFormatting.description")}
                  icon={<Icon icon="pen-to-square" size={ICON_SIZES.SM} />}
                />
                <CopyMenuItem
                  onClick={copyAsMarkdown}
                  label={ct("copyOptions.copyAsMarkdown.label")}
                  description={ct("copyOptions.copyAsMarkdown.description")}
                  icon={<Icon icon="hashtag" size={ICON_SIZES.SM} />}
                />
                <CopyMenuItem
                  onClick={exportAsPdf}
                  label={ct("copyOptions.exportAsPdf.label")}
                  description={ct("copyOptions.exportAsPdf.description")}
                  icon={<Icon icon="file-pdf" size={ICON_SIZES.SM} />}
                />
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </Tooltip>
      {copied && <span className="ml-2 text-xs text-600">{ct("copied")}</span>}
    </>
  );
}

const CopyMenuItem = ({
  children,
  onClick,
  label,
  description,
  icon,
}: {
  label?: string;
  description?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  onClick: () => void;
}) => {
  return (
    <DropdownMenuItem onClick={onClick} className="hover:bg-primary/10 pr-4">
      {!children && (
        <div className="flex items-start gap-2">
          {icon && <div className="pt-2">{icon}</div>}
          <div className="flex flex-col">
            {label && <div className="text-sm text-800">{label}</div>}
            {description && (
              <div className="text-xs text-600">{description}</div>
            )}
          </div>
        </div>
      )}
      {children}
    </DropdownMenuItem>
  );
};
